import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Grid,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Card,
  CardContent,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { ArrowLeft, Table, Calendar, Search, RotateCcw, Eye, Pencil, Check, CheckCircle } from 'lucide-react';
import { appointmentsApi } from '../../../api';
import useUIStore from '../../../store/useUIStore';

const AppointmentsPage = ({
  sess = { uid: '', role: 'user' },
}) => {
  const { showSuccess, showError } = useUIStore();
  const [appointments, setAppointments] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'calendar'
  const [statusFilter, setStatusFilter] = useState('all');
  const rowsPerPage = 10;

  useEffect(() => {
    fetchAppointments();
  }, []);


  useEffect(() => {
    applyFilters();
  }, [searchTerm, statusFilter, appointments]);

  const fetchAppointments = async () => {
    try {
      const response = await appointmentsApi.getAll();
      const data = response?.data?.data || response?.data || [];
      if (Array.isArray(data)) {
        setAppointments(data);
      } else {
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    }
  };


  const applyFilters = () => {
    let filtered = [...appointments];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.customer_name || '').toLowerCase().includes(search) ||
        (item.customer_number || '').toLowerCase().includes(search) ||
        (item.service || '').toLowerCase().includes(search) ||
        (item.employee_name || '').toLowerCase().includes(search)
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    }).replace(/,/g, '');
  };

  const getStatusColor = (status) => {
    const colors = {
      booked: '#0ea5e9',
      pending: '#ffc107',
      confirmed: '#28a745',
      cancelled: '#dc3545',
      completed: '#6c757d',
    };
    return colors[status] || '#6c757d';
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const response = await appointmentsApi.update(appointmentId, { status: newStatus });
      const result = response?.data || {};
      if (result.success || result.data) {
        showSuccess('Status updated successfully');
        fetchAppointments();
      } else {
        showError('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Error updating status');
    }
  };

  const handleViewDetails = (appointmentId) => {
    window.location.href = `/appointments/${appointmentId}`;
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Button variant="text" startIcon={<ArrowLeft />} href="/" sx={{ minWidth: 0, px: 0 }}>
          Back
        </Button>
        <Typography variant="h5">Appointments Management</Typography>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        mb={2}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" onClick={() => (window.location.href = '/appointments/create')}>
            New Appointment
          </Button>
          <Button variant="outlined" onClick={() => (window.location.href = '/appointment-services')}>
            Manage Services
          </Button>
          <Button variant="outlined" onClick={() => (window.location.href = '/employees')}>
            Manage Employees
          </Button>
          <Button variant="outlined" onClick={() => (window.location.href = '/branches')}>
            Manage Branches
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant={viewMode === 'table' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setViewMode('table')}
            startIcon={<Table size={16} />}
          >
            Table View
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setViewMode('calendar')}
            startIcon={<Calendar size={16} />}
          >
            Calendar View
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Search size={16} />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Search by customer, service, or employee..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="booked">Booked</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RotateCcw size={16} />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {viewMode === 'table' ? (
        <Card>
          <CardContent>
            <TableContainer>
              <MuiTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Customer Name</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Date &amp; Time</TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No appointments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentData.map((appointment, index) => (
                      <TableRow key={appointment.id} hover>
                        <TableCell>{startIndex + index + 1}</TableCell>
                        <TableCell>{appointment.customer_name || '-'}</TableCell>
                        <TableCell>{appointment.customer_number || '-'}</TableCell>
                        <TableCell>{appointment.service || '-'}</TableCell>
                        <TableCell>{appointment.employee_name || '-'}</TableCell>
                        <TableCell>{formatDateTime(appointment.appointment_date)}</TableCell>
                        <TableCell>{appointment.branch_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={appointment.status || 'pending'}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(appointment.status),
                              color: '#fff',
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Eye size={14} />}
                              onClick={() => handleViewDetails(appointment.id)}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<Check size={14} />}
                              onClick={() => handleStatusChange(appointment.id, 'confirmed')}
                              disabled={appointment.status === 'confirmed' || appointment.status === 'completed'}
                            >
                              Confirm
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </MuiTable>
            </TableContainer>

            {totalPages > 1 && (
              <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={3}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {currentPage} of {totalPages}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Calendar size={64} className="mb-3" color="#2ab63f" />
            <Typography variant="h6" gutterBottom>
              Calendar View
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Calendar view integration coming soon...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This will display appointments in a monthly/weekly calendar format for easy visualization.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AppointmentsPage;
